/**
 * Help & User Guide — reachable from the sidebar by every role, unlike every
 * other entry in `navModel.js`. There is no `help` screen in `iam.app_screen`
 * and none is needed: a user manual isn't a permission, so `visibleNav`
 * special-cases `screen: null` items to always render rather than this
 * module inventing a fake grant just to satisfy the usual gate.
 *
 * Content lives in `helpContent.js`, not here — this file is purely the
 * role-lens filter, search, and layout, built from this app's own components
 * (`Card`, `Badge`, `SearchInput`) so the guide feels like part of the
 * product instead of a second design system pasted into it.
 */
import { useMemo, useState } from 'react';
import { Card, Badge, SearchInput, EmptyState } from '../../components/common/index.jsx';
import { GROUPS, MATRIX, ROLES } from './helpContent.js';

const ROLE_KEYS = ['rep', 'manager', 'exec', 'admin', 'partner'];
const ROLE_ICON = { rep: 'bi-person', manager: 'bi-people', exec: 'bi-graph-up', admin: 'bi-shield-check', partner: 'bi-building' };
const STORAGE_KEY = 'entiovi.help.role';

function matches(section, term) {
  if (!term) return true;
  const haystack = [
    section.title, section.blurb,
    ...(section.howtos ?? []).flatMap((h) => [h.q, ...h.steps]),
    ...(section.notes ?? []).map((n) => n.text),
  ].join(' \n ').toLowerCase();
  return haystack.includes(term);
}

function ScopeChip({ role, value }) {
  if (!value) return <Badge tone="neutral" className="opacity-50">— {ROLES[role].label}</Badge>;
  return <Badge tone={ROLES[role].tone}>{ROLES[role].label} — {value}</Badge>;
}

function HowTo({ item }) {
  return (
    <details className="help-howto">
      <summary>
        {item.q}
        {item.badgeNew && <span className="pill pill-amber ms-2">New</span>}
      </summary>
      <ol>
        {item.steps.map((s, i) => <li key={i}>{s}</li>)}
      </ol>
    </details>
  );
}

function Note({ note }) {
  const icon = note.type === 'example' ? 'bi-flask' : 'bi-lightbulb';
  return (
    <div className={`help-note help-note-${note.type}`}>
      <i className={`bi ${icon}`} aria-hidden="true" />
      <span>{note.text}</span>
    </div>
  );
}

function Section({ section, viewRole }) {
  const relevant = section.roles.includes(viewRole);
  return (
    <Card
      className={`help-card mb-3 ${relevant ? '' : 'help-card-dim'}`}
      title={<>{section.title}{section.badgeNew && <span className="pill pill-amber ms-2">New</span>}</>}
      actions={
        section.scopes && (
          <div className="d-flex flex-wrap gap-1 justify-content-end">
            {ROLE_KEYS.map((r) => <ScopeChip key={r} role={r} value={section.scopes[r]} />)}
          </div>
        )
      }
    >
      {!relevant && (
        <div className="help-not-your-role">
          Not part of the {ROLES[viewRole].label} role — shown here for reference.
        </div>
      )}
      <p className="t-sm ink-2">{section.blurb}</p>
      {(section.howtos ?? []).map((h, i) => <HowTo key={i} item={h} />)}
      {(section.notes ?? []).map((n, i) => <Note key={i} note={n} />)}
    </Card>
  );
}

export default function HelpPage() {
  const [viewRole, setViewRole] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'rep'; } catch { return 'rep'; }
  });
  const [search, setSearch] = useState('');

  const pickRole = (role) => {
    setViewRole(role);
    try { localStorage.setItem(STORAGE_KEY, role); } catch { /* per-viewer convenience only */ }
  };

  const term = search.trim().toLowerCase();
  const filteredGroups = useMemo(
    () => GROUPS
      .map((g) => ({ ...g, sections: g.sections.filter((s) => matches(s, term)) }))
      .filter((g) => g.sections.length > 0),
    [term],
  );
  const noResults = term && filteredGroups.length === 0;
  const role = ROLES[viewRole];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumb"><span>Help</span></div>
          <h1 className="page-title">Help &amp; User Guide</h1>
          <p className="page-sub">Every screen, what it&apos;s for, and how to do the thing you opened it for.</p>
        </div>
      </div>

      <div className="help-role-switch mb-3">
        {ROLE_KEYS.map((r) => (
          <button
            key={r}
            type="button"
            className={`btn btn-sm-x ${viewRole === r ? 'btn-add' : 'btn-soft'}`}
            onClick={() => pickRole(r)}
          >
            <i className={`bi ${ROLE_ICON[r]} me-1`} aria-hidden="true" />{ROLES[r].label}
          </button>
        ))}
      </div>

      <div className={`help-banner help-banner-${role.tone} mb-4`}>
        <p className="fw-600 mb-1">{role.mission}</p>
        <p className="t-sm ink-2 mb-0">{role.landing}</p>
      </div>

      <div style={{ maxWidth: 320 }} className="mb-4">
        <SearchInput value={search} onChange={setSearch} placeholder="Search the guide…" />
      </div>

      {noResults ? (
        <EmptyState icon="bi-search" title="No matches" message="Try a different word, or clear the search to see the whole guide." />
      ) : (
        filteredGroups.map((g) => (
          <div key={g.group} className="help-group">
            <div className="eyebrow mt-4 mb-1">{g.group}</div>
            {g.intro && <p className="t-sm ink-3 mb-3">{g.intro}</p>}
            {g.sections.map((s) => <Section key={s.id} section={s} viewRole={viewRole} />)}
          </div>
        ))
      )}

      <Card title="Who can do what" subtitle="Deny always wins — if a role you hold denies something, no other role you also hold can override it." className="mt-4">
        <div className="table-x-wrap">
          <table className="table-x">
            <thead>
              <tr>
                <th>Screen</th>
                {ROLE_KEYS.map((r) => <th key={r}>{ROLES[r].label}</th>)}
              </tr>
            </thead>
            <tbody>
              {MATRIX.map((row) => (
                <tr key={row.area}>
                  <td className="fw-600">{row.area}</td>
                  {ROLE_KEYS.map((r) => (
                    <td key={r}>{row[r] ? <span className={`help-scope help-scope-${ROLES[r].tone}`}>{row[r]}</span> : <span className="text-muted">—</span>}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
